import { getHasAnyProjects } from "@frontend-shared/utils";
import { Search } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { PATHS } from "@/constants/paths";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

import { getSearchShortcutLabel } from "./global-search/utils/get-search-shortcut-label";
import { HeaderTourButton } from "./HeaderTourButton";
import { ProjectMenu } from "./ProjectMenu";

interface IHeaderProps {
  isSearchChatsOpen: boolean;
  setIsSearchChatsOpen: (isOpen: boolean) => void;
  // NOTE: NSAI-5822 — a callback ref for the element pages portal their
  // breadcrumb into. AppLayout owns the state because the provider has to wrap
  // both this header and the routed page that fills it.
  breadcrumbSlotRef: (element: HTMLDivElement | null) => void;
}

const HeaderComponent = ({
  isSearchChatsOpen,
  setIsSearchChatsOpen,
  breadcrumbSlotRef,
}: IHeaderProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");
  const { projects } = useAuth();
  const { isOpen } = useSidebar();

  const hasAnyProjects = getHasAnyProjects(projects);
  // NOTE: NSAI-6589 — the Cmd/Ctrl+K hint rides on the tooltip; the platform
  // decides which modifier the user actually presses.
  const searchLabel = `${t("trigger_label")} (${getSearchShortcutLabel()})`;

  return (
    <div
      data-testid="header"
      // NOTE: NSAI-6650 — the breadcrumb starts on the same line as the page
      // below it. A page's content sits behind three insets: `MainContent`'s
      // outer padding (none while the sidebar is open, 16px beside the
      // collapsed rail), its inner `px-4`, and the page's own `p-5`. That is
      // 36px, or 52px beside the rail, and the header used to stop at 16px, so
      // every breadcrumb hung 20px left of the title under it. Keep these two
      // values in step with `MainContent`.
      className={cn(
        "h-[var(--top-navbar-height)] ps-9 pe-4 w-full bg-background flex items-center justify-between gap-4",
        !isOpen && "md:ps-[3.25rem]",
      )}
    >
      {/* NOTE: NSAI-5822 — the leading side is the page's breadcrumb, sitting
          directly beside the sidebar's collapse control. It used to be a row of
          its own below this strip, which left a band of empty space above every
          page. The logo only appears here on the no-projects screen, where
          there is no sidebar to carry it. */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {!hasAnyProjects && (
          <Link to={PATHS.CHAT}>
            <Logo className="h-8 w-auto" />
          </Link>
        )}
        <div
          ref={breadcrumbSlotRef}
          data-testid="header-breadcrumb-slot"
          className="flex min-w-0 flex-1 items-center"
        />
      </div>
      {/* NOTE: NSAI-5822 — the tour entry point lives here rather than in the
          account menu, where NSAI-4345 had put it, so it is in one place rather
          than both. This header is the same on every route inside the app
          layout, settings included.

          NOTE: NSAI-6259 — the corner was emptied and refilled. Out went the
          theme picker and the notifications bell; both are rows of the account
          menu now, and the bell's unread count was a second copy of the one on
          the account avatar. In came the project / organization selector,
          promoted from a submenu of that same account menu, where it read as
          one more option in a dropdown rather than as the control that decides
          what the whole page is about. What is left is: search, the tour, the
          project.

          The chat's floating share badge is the one thing still to leave. It
          is not in this strip — it floats in the corner of the thread itself
          (Chat.tsx, `shareBadge`) — and where it goes instead has not been
          agreed with design yet, so removing it now would take the only way to
          share a chat with it. It stays until that call is made. */}
      {hasAnyProjects && (
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            id="sidebar-search"
            data-testid="sidebar-search"
            variant="ghost"
            size="icon"
            aria-label={t("trigger_label")}
            aria-keyshortcuts="Meta+K Control+K"
            title={searchLabel}
            onClick={() => setIsSearchChatsOpen(!isSearchChatsOpen)}
            className="h-9 w-9 rounded-full text-primary transition-[background-color,color,transform] duration-200 ease-out hover:text-accent-foreground active:scale-95 motion-reduce:transition-none motion-reduce:active:scale-100"
          >
            <Search className="h-5 w-5" />
          </Button>
          <HeaderTourButton />
          <ProjectMenu />
        </div>
      )}
    </div>
  );
};

export const Header = memo(HeaderComponent);
